import { CardErrorMessage, Table } from '../util';
import { createTimezonedDate } from '../util/date';
import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { sortByDateCreated } from '../util/date/dateUtils';
import { ChangeSet } from '../models/ChangeSet';
import IconWihTextOnTheRight from '../util/icons/IconWihTextOnTheRight';
import serviceMethods from '../services/service_methods';
import ViewChecklistAnswersModal from './ViewChecklistAnswersModal';
const dateToLocale = date => {
  return createTimezonedDate(new Date(date)).toCustomLocaleTimeString();
};
const ObjectChangeSetsListWithSnapshots = _ref => {
  let {
    object,
    reviewType,
    resetData,
    showAllReviewTypes = false
  } = _ref;
  const {
    t
  } = useTranslation();
  const [viewChecklistModalShow, setViewChecklistModalShow] = useState(false);
  const [selectedChecklistAnswers, setSelectedChecklistAnswers] = useState([]);
  const isUkMarket = process.env.PUBLIC_URL !== '/de';
  const interval = useMemo(() => serviceMethods.useHttpInterval({
    callback: resetData,
    timeoutMilliseconds: 2000
  }), [resetData]);
  useEffect(() => {
    const everyReportIsSuccess = object?.change_sets?.every(c => {
      if (!c.report_snapshots || c.report_snapshots.length === 0) {
        return true;
      }
      return c.report_snapshots.every(s => s.rnd_report?.pdf_generation_status === 'success');
    });
    if (everyReportIsSuccess && interval.started) {
      interval.clear();
    } else if (!everyReportIsSuccess && !interval.started) {
      interval.start();
    }
    return () => {
      interval.clear();
    };
  }, [interval, object.change_sets, resetData]);
  const handleViewChecklist = checklistAnswers => {
    setSelectedChecklistAnswers(checklistAnswers);
    setViewChecklistModalShow(true);
  };
  const buildColumns = (date, author, changeSetModel, statusContent, reportContent) => {
    const columns = [dateToLocale(date), author || 'Unknown'];
    if (isUkMarket) {
      columns.push(changeSetModel.getInternalReviewTypeText());
    }
    columns.push(statusContent, reportContent);
    if (isUkMarket) {
      columns.push(changeSetModel.getChecklistLink());
    }
    return columns;
  };
  const rows = (object.change_sets || []).filter(changeSet => showAllReviewTypes || changeSet.review_type === reviewType).sort(sortByDateCreated).map(changeSet => {
    const changeSetModel = new ChangeSet({
      changeSet,
      t,
      onViewChecklist: handleViewChecklist
    });
    if (changeSetModel.isCostOnly()) {
      return [{
        columns: buildColumns(changeSet.created_at, changeSet.author?.name, changeSetModel, /*#__PURE__*/React.createElement(IconWihTextOnTheRight, {
          icon: "done",
          text: t('cost_only_completed')
        }), /*#__PURE__*/React.createElement("span", {
          className: "text-muted"
        }, t('no_pdf_required')))
      }];
    }
    const snapshots = changeSetModel.getSnapshots();
    if (snapshots.length === 0) {
      return [{
        columns: buildColumns(changeSet.created_at, changeSet.author?.name, changeSetModel, /*#__PURE__*/React.createElement(IconWihTextOnTheRight, {
          icon: "engineering",
          text: t('review_in_progress')
        }), /*#__PURE__*/React.createElement("span", {
          className: "text-muted"
        }, t('awaiting_finalization')))
      }];
    }
    return snapshots.map(snapshot => {
      const snapshotStatus = changeSetModel.getSnapshotStatus(snapshot);
      return {
        columns: buildColumns(snapshot.status_datetime, changeSetModel.getSnapshotActorName(snapshot), changeSetModel, /*#__PURE__*/React.createElement(IconWihTextOnTheRight, {
          icon: snapshotStatus.icon,
          text: t(snapshotStatus.text)
        }), changeSetModel.getSnapshotDownloadLinkOrPendingStatus(snapshot))
      };
    });
  }).flat();
  if (!object.change_sets || object.change_sets.length === 0) {
    return /*#__PURE__*/React.createElement(CardErrorMessage, {
      errorMessage: t('no_changes_for_this_object_yet')
    });
  }
  const headers = [t('date'), t('author')];
  if (isUkMarket) {
    headers.push(t('internal_review_type'));
  }
  headers.push(t('status'), t('report'));
  if (isUkMarket) {
    headers.push(t('checklist'));
  }
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Row, {
    className: "scrollable",
    style: {
      height: '220px'
    }
  }, /*#__PURE__*/React.createElement(Col, {
    md: 12
  }, /*#__PURE__*/React.createElement(Table, {
    className: "table-scrollable-x",
    headers: headers,
    rows: rows,
    showControls: false
  }))), isUkMarket && /*#__PURE__*/React.createElement(ViewChecklistAnswersModal, {
    show: viewChecklistModalShow,
    onHide: () => setViewChecklistModalShow(false),
    checklistAnswers: selectedChecklistAnswers
  }));
};
export default ObjectChangeSetsListWithSnapshots;